<template>
	<SimpleModal @close="close">
		<div
			class="omnibox-container"
			@keydown.prevent.down="handleDown"
			@keydown.prevent.up="handleUp"
			@keydown.prevent.tab.exact="handleDown"
			@keydown.prevent.shift.tab="handleUp"
			@keydown.prevent.enter="launchSelected()"
			@click="$refs.input && $refs.input.focus()"
		>
			<div class="omnibox-search-wrapper">
				<Input
					v-model="search"
					:icon="SearchIcon"
					:placeholder="$t('contacts_userpicker_search_placeholder')"
					autoFocus
					ref="input"
				/>
				<div class="search-options" ref="listcontainer">
					<div v-for="(item, index) in filtered" :key="item.id">
						<TextDivider
							v-if="showCategory(index, item.category)"
							:text="item.category"
							:textStyle="{
								color: '#9498a3',
								fontSize: '14px',
								fontFamily: 'Avenir-Medium',
							}"
							style="margin: 16px 0"
						/>
						<a
							class="search-item"
							:class="{ selected: index === selected }"
							@mousemove="handleHover(index)"
							@mouseleave="hovering = false"
							@click.prevent.stop="launchSelected()"
						>
							<div v-if="item.isThread" class="thread-icon">
								<ReelIcon type="solid" />
							</div>
							<GroupIcon v-else-if="item.isChat" :chatId="item.id" />
							<ProfilePizza
								v-if="item.isMultiDM"
								:userIds="item.dmMemberIds"
								:size="24"
								showTotal
								alphabetical
							/>
							<ProfilePic
								v-if="item.isDM && !item.isMultiDM"
								:user="item.partner"
								size="24px"
								enableOnline
							/>
							<component v-if="item.icon" :is="item.icon" class="action-icon" />
							<span v-text="item.name" />
						</a>
					</div>
				</div>
			</div>
		</div>
	</SimpleModal>
</template>

<script>
import store, {
	getCategories,
	getChatsForCategory,
	getChatsWithNoCategory,
	getThreadChats,
	getDMChats,
	canSendAnnouncements,
	getCurrentChat,
	getUser,
	getUsersForBubble,
} from '@/store'
import { sortBubblesByTitle } from '@/helpers/utils'
import {
	SimpleModal,
	TextDivider,
	Input,
	GroupIcon,
	ProfilePizza,
	ProfilePic,
} from '@/components/common'
import {
	CommentsIcon,
	UserGroupIcon,
	BullhornIcon,
	CalendarCheckIcon,
	LinkIcon,
	ReelIcon,
	SearchIcon,
	SquarePollHorizontalIcon,
} from '@/icons'
import scrollIntoView from 'scroll-into-view-if-needed'
import { redirectToRecentsOrChat } from '@/helperMethods'
import { markRaw } from 'vue'
import { chatCan, CREATE_POLLS } from '@/helpers/permissions'
import { orgFlag, POLLS } from '@/helpers/flags'
import Fuse from 'fuse.js'

export default {
	components: {
		Input,
		SimpleModal,
		TextDivider,
		CommentsIcon,
		UserGroupIcon,
		BullhornIcon,
		CalendarCheckIcon,
		LinkIcon,
		ReelIcon,
		SquarePollHorizontalIcon,
		GroupIcon,
		ProfilePizza,
		ProfilePic,
	},
	data() {
		return {
			store,
			search: '',
			selected: 0,
			SearchIcon: markRaw(SearchIcon),
		}
	},

	emits: ['close'],

	watch: {
		search: function (val) {
			this.selected = 0
			this.scrollIntoViewIfNeeded()
		},
	},

	computed: {
		actions() {
			const actions = []
			if (store.checkOrgPermission('create_dm')) {
				actions.push({
					icon: CommentsIcon,
					name: this.$t('chat_drawer_addbubble_directmessage'),
					action: 'create_direct_message',
				})
			}
			if (store.checkOrgPermission('create_group')) {
				actions.push({
					icon: UserGroupIcon,
					name: this.$t('chat_drawer_addbubble_create'),
					action: 'create_group',
				})
			}
			actions.push({
				icon: LinkIcon,
				name: this.$t('groupinvites_join_title'),
				action: 'enter_group_code',
			})
			if (canSendAnnouncements()) {
				actions.push({
					icon: BullhornIcon,
					name: this.$t('chat_drawer_addbubble_announcement'),
					action: 'send_announcement',
				})
			}
			const currentChat = getCurrentChat()
			const canCreatePoll = chatCan(currentChat, CREATE_POLLS) && orgFlag(POLLS)
			if (canCreatePoll) {
				actions.push({
					icon: SquarePollHorizontalIcon,
					name: this.$t('polls_create_title'),
					action: 'create_poll',
				})
			}
			if (store.tasksEnabled()) {
				actions.push({
					icon: CalendarCheckIcon,
					name: this.$t('chat_drawer_addbubble_task'),
					action: 'create_task',
				})
			}
			return actions.map((a) => ({
				...a,
				category: this.$t('chat_drawer_addbubble_title'),
			}))
		},

		categories() {
			return getCategories()
				.map((cat) => {
					return getChatsForCategory(cat.id)
						.map((bubble) => this.assignCategoryAndName(bubble, cat.title))
						.sort(sortBubblesByTitle)
				})
				.flat()
		},

		noncategories() {
			return getChatsWithNoCategory()
				.map((bubble) =>
					this.assignCategoryAndName(
						bubble,
						this.$t('chat_drawer_section_bubbles_uncatagorized')
					)
				)
				.slice()
				.sort(sortBubblesByTitle)
		},

		dms() {
			return getDMChats()
				.map((bubble) =>
					this.assignCategoryAndName(
						bubble,
						this.$t('chat_drawer_section_directmessages')
					)
				)
				.slice()
				.sort(sortBubblesByTitle)
		},

		threads() {
			return getThreadChats()
				.map((bubble) =>
					this.assignCategoryAndName(
						bubble,
						this.$t('chat_threadbrowser_title')
					)
				)
				.slice()
				.sort(sortBubblesByTitle)
		},

		allMenuItems() {
			return [
				...this.actions,
				...this.categories,
				...this.noncategories,
				...this.dms,
				...this.threads,
			]
		},

		fuseIndex() {
			return new Fuse(this.allMenuItems, {
				threshold: 0.3,
				ignoreDiacritics: true,
				keys: ['name'],
			})
		},

		filtered() {
			if (!this.search) return this.allMenuItems
			const matches = new Set(
				this.fuseIndex.search(this.search, { limit: 30 }).map((r) => r.item)
			)
			const q = this.search.toLowerCase()
			const inSource = this.allMenuItems.filter((item) => matches.has(item))
			// Float categories that contain any literal prefix match above
			// categories with only fuzzy matches. Keeping the tier at the
			// category level (not per-item) preserves contiguous dividers.
			const prefixCategories = new Set(
				inSource
					.filter((item) => item.name.toLowerCase().startsWith(q))
					.map((item) => item.category)
			)
			return inSource
				.map((item, i) => ({
					item,
					i,
					tier: prefixCategories.has(item.category) ? 0 : 1,
				}))
				.sort((a, b) => {
					if (a.tier !== b.tier) return a.tier - b.tier
					return a.i - b.i
				})
				.map((x) => x.item)
		},
	},

	methods: {
		assignCategoryAndName(chat, category) {
			return {
				id: chat.id,
				isChat: !chat.isdm,
				isThread: chat.is_thread,
				isDM: chat.isdm,
				isMultiDM: chat.isMultiDM,
				partner: chat.isDM ? getUser(chat.dmpartner_id) : null,
				dmMemberIds: chat.isMultiDM
					? getUsersForBubble(chat.id).map((m) => m.id)
					: [],
				action: 'jump_to_group',
				category,
				name: chat.is_thread
					? chat.title || chat.displayname
					: chat.displayname,
			}
		},

		close() {
			this.$emit('close')
		},

		handleDown() {
			this.hovering = false
			this.selected === this.filtered.length - 1
				? this.filtered.length - 1
				: (this.selected += 1)
			this.scrollIntoViewIfNeeded()
		},

		handleHover(index) {
			this.hovering = true
			this.selected = index
		},

		handleUp() {
			this.hovering = false
			this.selected === 0 ? 0 : (this.selected -= 1)
			this.scrollIntoViewIfNeeded()
		},

		scrollIntoViewIfNeeded() {
			const target = this.$refs.listcontainer.querySelector('.selected')
			if (!target) return

			scrollIntoView(target, {
				scrollMode: 'if-needed',
				block: 'center',
				inline: 'nearest',
				behavior: 'smooth',
			})
		},

		launchSelected() {
			const action = this.filtered[this.selected].action
			switch (action) {
				case 'search':
					this.$router.push({
						name: 'search',
						query: { q: this.search },
					})
					break
				case 'jump_to_group':
					const groupId = this.filtered[this.selected].id
					redirectToRecentsOrChat(groupId)
					break
				case 'create_poll':
					EventBus.$emit('show-create-poll', true)
					break
				default:
					EventBus.$emit('omniboxAction', action)
					break
			}
			this.close()
		},

		showCategory(idx, category) {
			const first_index_of_category = this.filtered.findIndex(
				(item) => item.category === category
			)
			return first_index_of_category === idx
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.omnibox-container {
	width: 590px;
	background: var(--neutral-0);
	color: var(--neutral-800);
	padding: 60px 40px 40px 40px;
	@include flex-column;

	.pronto-input {
		margin-bottom: 16px;
	}

	.search-options {
		height: 300px;
		overflow-y: auto;
		outline: 0;
		flex-grow: 1;

		.search-item {
			cursor: pointer;
			@include flex(12px);
			height: 35px;
			padding: 0 8px;
			border-radius: 6px;
			outline: 0;
			text-decoration: none;
			&.selected {
				background-color: var(--teal-100);
				> span,
				.action-icon {
					color: var(--teal-300);
				}
			}
			> span {
				display: inline-block;
				color: var(--neutral-800);
				@include ellipsis;
				max-width: 480px;
			}
			.action-icon {
				height: 18px;
				margin-right: 10px;
				vertical-align: top;
			}
			.pronto-group-icon {
				flex-shrink: 0;
				font-size: 24px;
			}
			.thread-icon {
				flex-shrink: 0;
				width: 24px;
				height: 24px;
				background-color: var(--neutral-200);
				@include flex-center;
				@include radius;
				.pronto-icon {
					color: var(--neutral-500);
					font-size: 14px;
					transform: rotate(30deg);
				}
			}
			.profile-pizza {
				flex-shrink: 0;
			}
			.profile-pic-avatar {
				flex-shrink: 0;
				:deep(.online-indicator) {
					--online-size: 10px;
				}
			}
		}
	}

	:deep(.text-divider-container) {
		.text {
			@include ellipsis;
			max-width: 300px;
			flex-grow: 1;
			flex-shrink: 0;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.omnibox-container {
		background: var(--neutral-0);
		.text-divider-container {
			.line {
				border-color: var(--neutral-40040);
			}
		}

		.search-options {
			height: 300px;
			overflow-y: auto;
			outline: 0;
			flex-grow: 1;
			.search-item {
				&.selected {
					background-color: var(--neutral-300);
					> span,
					.action-icon {
						color: var(--neutral-800);
					}
				}
				.online-indicator {
					border-color: var(--neutral-300);
				}
			}
		}
	}
}

@include overrideContrast {
	.omnibox-container {
		.search-options {
			.search-item {
				&.selected {
					> span,
					.action-icon {
						color: var(--neutral-100);
					}
				}
			}
		}
	}
}
</style>
